import { ReactElement, useEffect, useRef } from 'react';

import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import { MAX_PLAYERS_PER_EVENT } from '../../constants/limits';
import { useAuth } from '../../contextProviders/AuthProvider';
import Event, { ALL_ATTENDEE_OPTIONS, ATTENDEE_OPTIONS } from '../../types/Event';
import Season from '../../types/Season';
import GratiColorPicker from '../common/GratiColorPicker';
import SeasonSelector from '../season/SeasonSelector';

import EventMembershipTypeEditor from './EventMembershipTypeEditor';
import { FieldTarget } from './eventPreviewTypes';
import EventSignupTimeEditor from './EventSignupTimeEditor';

export interface EventAdvancedSettingsEditorProps {
  event: Event;
  isGolf: boolean;
  isTeamLeague?: boolean;
  isCatchAllLeague?: boolean;
  leagueHasDivisions?: boolean; // when true, show the division grouping checkbox
  defaultTeamSize?: number;
  /** Whether to include signup time editor (default: true) */
  includeSignup?: boolean;
  onUpdate: (updates: Partial<Event>) => void;
  onSeasonChange: (season: Season | undefined) => void;
  /** Field to focus when the editor expands */
  focusTarget?: FieldTarget | null;
  /** Called after focus has been applied */
  onFocusComplete?: () => void;
}

export default function EventAdvancedSettingsEditor(props: EventAdvancedSettingsEditorProps): ReactElement {
  const { event, isGolf, isTeamLeague = false, isCatchAllLeague = false, leagueHasDivisions = false, defaultTeamSize, includeSignup = true, onUpdate, onSeasonChange, focusTarget, onFocusComplete } = props;
  const { isGod } = useAuth();
  const descriptionRef = useRef<HTMLInputElement>(null);

  // Handle focus target from parent
  useEffect(() => {
    if (focusTarget === 'color') {
      onFocusComplete?.();
    } else if (focusTarget === 'description' && descriptionRef.current) {
      descriptionRef.current.focus();
      onFocusComplete?.();
    } else if (focusTarget === 'signupDates') {
      // Signup dates are in a child component, just acknowledge
      onFocusComplete?.();
    }
  }, [focusTarget, onFocusComplete]);

  return (
    <Stack spacing={2}>
      {/* Hide SeasonSelector for catch-all leagues (season is auto-selected) */}
      {!isCatchAllLeague && (
        <SeasonSelector
          value={event.seasonId}
          leagueId={event.leagueId}
          orgId={event.orgId}
          onChange={onSeasonChange}
          sx={{ maxWidth: '20em' }}
        />
      )}
      <TextField
        label="Golf Genius Event ID (GGID)"
        value={event.ggid || ''}
        onChange={(e) => onUpdate({ ggid: e.target.value || undefined })}
        sx={{ maxWidth: '20em' }}
      />
      <Stack direction="row" spacing={2} sx={{
        alignItems: "flex-start"
      }}>
        <TextField
          multiline
          minRows={2}
          maxRows={6}
          label="Description"
          inputRef={descriptionRef}
          value={event.description || ''}
          onChange={(e) => onUpdate({ description: e.target.value })}
          sx={{ flex: 1, minWidth: 0 }}
        />
        <Stack
          spacing={0.5}
          sx={{
            alignItems: "center",
            pt: 1,
            flexShrink: 0
          }}>
          <Typography variant="body2" sx={{
            color: "text.secondary"
          }}>Color</Typography>
          <GratiColorPicker
            value={event.color}
            onChange={(color) => onUpdate({ color })}
          />
        </Stack>
      </Stack>
      <EventMembershipTypeEditor
        event={event}
        isGolf={isGolf}
        isTeamLeague={isTeamLeague}
        defaultTeamSize={defaultTeamSize}
        onUpdate={onUpdate}
        focusTarget={focusTarget}
        onFocusComplete={onFocusComplete}
      />
      {includeSignup && <EventSignupTimeEditor event={event} isGolf={isGolf} isTeamLeague={isTeamLeague} onUpdate={onUpdate} />}
      {isGolf && (
        <Stack>
          <Typography variant="body1">Allow player to choose:</Typography>
          <Stack direction="row">
            {([
              { option: ATTENDEE_OPTIONS.NUM_HOLES, label: '9 or 18 holes' },
              { option: ATTENDEE_OPTIONS.WALK_RIDE, label: 'Ride or walk' },
              { option: ATTENDEE_OPTIONS.PLAY_GAME, label: 'Compete or social' },
              { option: ATTENDEE_OPTIONS.FOLLOW_FIELD, label: 'Follow the field (no game)' },
              { option: ATTENDEE_OPTIONS.TEE_CHOICE, label: 'Tees' },
            ] as const).map(({ option, label }) => (
              <FormControlLabel
                key={option}
                labelPlacement="top"
                control={
                  <Checkbox
                    checked={(event.attendeeOptions ?? ALL_ATTENDEE_OPTIONS).includes(option)}
                    onChange={(e) => {
                      const current = event.attendeeOptions ?? ALL_ATTENDEE_OPTIONS;
                      onUpdate({
                        attendeeOptions: e.target.checked
                          ? [...current, option]
                          : current.filter((o) => o !== option),
                      });
                    }}
                  />
                }
                label={label}
              />
            ))}
          </Stack>
          {(event.attendeeOptions ?? ALL_ATTENDEE_OPTIONS).includes(ATTENDEE_OPTIONS.FOLLOW_FIELD) && (
            <TextField
              id="maxFieldFollowers"
              label="Max field followers"
              helperText="Limit on non-competing followers once the roster is full. Leave blank for unlimited."
              size="small"
              slotProps={{ htmlInput: { inputMode: 'numeric', type: 'number', min: 1, max: MAX_PLAYERS_PER_EVENT } }}
              sx={{ width: '14em', mt: 1 }}
              value={event.maxFieldFollowers && event.maxFieldFollowers > 0 ? String(event.maxFieldFollowers) : ''}
              onChange={(e) => {
                const v = e.target.value;
                if (v === '') {
                  onUpdate({ maxFieldFollowers: -1 });
                  return;
                }
                const n = parseInt(v, 10);
                if (!isNaN(n) && n >= 1 && n <= MAX_PLAYERS_PER_EVENT) {
                  onUpdate({ maxFieldFollowers: n });
                }
              }}
            />
          )}
        </Stack>
      )}
      {isGod && leagueHasDivisions && (
        <FormControlLabel
          control={
            <Checkbox
              checked={event.isGroupedByDivision ?? false}
              onChange={(e) => onUpdate({ isGroupedByDivision: e.target.checked })}
            />
          }
          label="Group attendees and tee sheet by division"
        />
      )}
    </Stack>
  );
}
